import React from "react";
import "./designplan.scss";


// ─── Design Plan Window ──────────────────────────────────────────────
// A full-width "plan window" card (Claude-Code-in-VSCode / MuleSoft vibe)
// rendered in the center chat column. Shows the structured implementation plan
// the hub returns as `type: "design_plan_pending"` and lets the user approve /
// edit in plain English / undo / ask questions before the diagram is drawn.
//
// Props:
//   data        { plan, round, maxRounds, openDecisions, lastEdit, canEdit, editHint }
//   fallbackMd  the raw markdown message (used when `plan` is missing/thin)
//   renderMd    renderMdText from the parent (markdown fallback renderer)
//   loading     disables actions while a turn is in flight
//   onAction    (text, { noBubble }) => void  — posts prompt to the hub
//
// Interaction parity with ArchitecturalReviewCard: approve / undo / show / a
// question send WITHOUT a user bubble; a plain-English change DOES push one.

const C = {
  bg:        "#0e1219",
  panel:     "#121825",
  border:    "rgba(255,255,255,0.08)",
  borderHi:  "rgba(59,127,255,0.30)",
  accent:    "#3b7fff",
  text:      "#dce8ff",
  sub:       "#aac0dc",
  muted:     "#7a93bc",
  faint:     "#3d5273",
  good:      "#00c98d",
  warn:      "#f5a623",
  chip:      "rgba(59,127,255,0.12)",
  hi:        "rgba(0,201,141,0.14)", // changed-id highlight
};

// Replies that should NOT push a user bubble (match the spec: approve / undo /
// show / questions are "free" actions; a plain-English change is a real edit).
function isNoBubble(text) {
  const t = (text || "").trim().toLowerCase();
  return t === "approve" || t === "undo" || /^show\b/.test(t) || t.endsWith("?");
}

function Section({ title, hint, children }) {
  return (
    <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: "0.06em", marginBottom: hint ? 2 : 8, textTransform: "uppercase" }}>
        {title}
      </div>
      {hint && <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  );
}

function SourceTag({ source }) {
  if (!source) return null;
  const label = {
    you: "you", answered: "you answered", accepted: "accepted default",
    assumed: "assumed by Intelog", prompt: "from your request", resolved: "resolved",
  }[source] || source;
  const assumed = source === "assumed";
  return (
    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 4,
      background: assumed ? "rgba(245,166,35,0.14)" : "rgba(59,127,255,0.12)", color: assumed ? C.warn : C.accent }}>
      {label}
    </span>
  );
}

function SubStep({ s, changed }) {
  const indent = (s.depth || 0) > 0 || s.kind === "branch";
  const isBranch = s.kind === "branch";
  const isNote = s.kind === "note" || s.kind === "effect";
  return (
    <div style={{
      display: "flex", gap: 8, fontSize: 12, lineHeight: 1.55, padding: "3px 6px", marginLeft: indent ? 18 : 0,
      color: isNote ? C.muted : C.text, fontStyle: isNote ? "italic" : "normal",
      background: changed ? C.hi : "transparent", borderRadius: 5,
    }}>
      <span style={{ color: C.faint, fontWeight: 700, minWidth: 30, fontVariantNumeric: "tabular-nums" }}>{s.id}</span>
      <span>
        {/* branch `text` already starts with "If …" — do not prefix another "If" */}
        {isBranch ? <span style={{ color: C.warn, fontWeight: 600 }}>{s.text}</span> : s.text}
        {s.consequence ? <span style={{ color: C.sub }}> → {s.consequence}</span> : null}
        {s.because ? <span style={{ color: C.muted }}> {" "}<em>(because {s.because})</em></span> : null}
      </span>
    </div>
  );
}

function FlowCard({ step, changedIds, defaultOpen }) {
  const flowChanged = changedIds.has(String(step.id));
  // Collapsed by default so the plan is scannable; click the header to expand steps.
  const [open, setOpen] = React.useState(!!defaultOpen);
  const stepCount = (step.subSteps || []).length;
  return (
    <div style={{ marginBottom: 10, border: `1px solid ${flowChanged ? "rgba(0,201,141,0.35)" : C.border}`,
      borderRadius: 8, overflow: "hidden", background: C.bg }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ padding: "9px 12px", background: "rgba(59,127,255,0.05)", borderBottom: open ? `1px solid ${C.border}` : "none", cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: C.muted, fontSize: 11, width: 12, flexShrink: 0 }}>{open ? "▾" : "▸"}</span>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, flex: 1 }}>Flow {step.id}: {step.title}</div>
          <span style={{ fontSize: 10, color: C.faint, flexShrink: 0 }}>{stepCount} step{stepCount !== 1 ? "s" : ""}</span>
        </div>
        {step.trigger && (
          <div style={{ marginTop: 5, marginLeft: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: C.chip, color: C.accent }}>
              {step.trigger}
            </span>
          </div>
        )}
        {open && step.owner && <div style={{ fontSize: 11, color: C.muted, marginTop: 5, marginLeft: 20 }}>Runs in: {step.owner}</div>}
        {open && step.quote && <div style={{ fontSize: 11, color: C.faint, marginTop: 3, marginLeft: 20, fontStyle: "italic" }}>“{step.quote}”</div>}
      </div>
      {open && (
        <div style={{ padding: "6px 8px" }}>
          {(step.subSteps || []).map((s) => (
            <SubStep key={s.id} s={s} changed={changedIds.has(String(s.id))} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DesignPlanWindow({ data, fallbackMd, renderMd, loading, onAction }) {
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [maximized, setMaximized] = React.useState(false); // full-width "IDE view" overlay
  // Full-screen overlay style when maximized; normal inline card otherwise.
  const rootStyle = maximized
    ? { position: "fixed", inset: 0, zIndex: 1000, margin: 0, borderRadius: 0, maxHeight: "100vh", border: "none", background: C.panel }
    : { marginTop: 12, border: `1px solid ${C.borderHi}`, borderRadius: 10, overflow: "hidden", background: C.panel };
  // In-window echo of the free-text edits/questions the user submitted this
  // session (they do NOT appear as chat bubbles — the exchange stays here).
  const [requests, setRequests] = React.useState([]);

  // The card stays mounted across edit rounds (updated in place). When a new plan
  // payload arrives, release the local "busy" lock so actions re-enable.
  React.useEffect(() => {
    console.log("[DesignPlanWindow] data received:", {
      hasPlan: !!(data && data.plan),
      round: data && data.round,
      maxRounds: data && data.maxRounds,
      openDecisions: data && (data.openDecisions || (data.plan && data.plan.needsDecision)),
      lastEdit: data && data.lastEdit,
      data,
    });
    setBusy(false);
  }, [data]);

  const plan = data && data.plan;
  const round = data && data.round;
  const maxRounds = data && data.maxRounds;
  const lastEdit = (data && data.lastEdit) || null;
  const changedIds = new Set((lastEdit && lastEdit.changedIds ? lastEdit.changedIds : []).map(String));

  const act = (value, opts) => {
    if (loading || busy) { console.warn("[DesignPlanWindow] act ignored (busy/loading):", { value, loading, busy }); return; }
    console.log("[DesignPlanWindow] act →", value, opts);
    setBusy(true);
    onAction(value, opts);
  };
  const sendTyped = () => {
    const t = text.trim();
    if (!t) return;
    console.log("[DesignPlanWindow] sendTyped →", t);
    setText("");
    setRequests((prev) => [...prev, t]);
    act(t, { noBubble: isNoBubble(t) });
  };

  // Fallback: no structured plan — render the markdown message with an action bar.
  if (!plan) {
    return (
      <div className="sh-design-plan-card" style={{ marginTop: 12, border: `1px solid ${C.borderHi}`, borderRadius: 10, overflow: "hidden", background: C.panel }}>
        {fallbackMd && <div style={{ padding: "14px 16px" }}>{renderMd ? renderMd(fallbackMd) : fallbackMd}</div>}
        <ActionBar text={text} setText={setText} sendTyped={sendTyped} act={act} loading={loading} busy={busy} canEdit={data && data.canEdit !== false} requests={requests} />
      </div>
    );
  }

  const steps = plan.steps || [];
  const flows = steps.filter((s) => s.kind === "flow");
  const systems = steps.find((s) => s.kind === "systems");
  const effects = steps.find((s) => s.kind === "effects");
  const notes = steps.find((s) => s.kind === "notes");
  const counts = plan.counts || {};
  const needsDecision = (data && data.openDecisions && data.openDecisions.length ? data.openDecisions : plan.needsDecision) || [];

  return (
    <div className="sh-design-plan-card" style={rootStyle}>

      {/* Header */}
      <div style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, background: "rgba(59,127,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>📋 {plan.title || "Implementation Plan"}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {/* plan.scope already contains the counts ("3 flows · 13 systems · …"); render it verbatim.
                  Only fall back to a computed counts line when scope is absent. */}
              {plan.scope
                ? plan.scope
                : `${flows.length} flow${flows.length !== 1 ? "s" : ""} · ${(systems && (systems.subSteps || []).length) || 0} systems`}
            </div>
          </div>
          <button
            onClick={() => setMaximized((m) => !m)}
            title={maximized ? "Exit full screen" : "Expand to full screen"}
            style={{ flexShrink: 0, padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.sub, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
          >{maximized ? "⤡ Collapse" : "⤢ Expand"}</button>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
          {counts.confirmed != null && <Pill label={`${counts.confirmed} confirmed`} color={C.good} />}
          {counts.assumed != null && <Pill label={`${counts.assumed} assumptions`} color={C.warn} />}
          {needsDecision.length > 0 && <Pill label={`${needsDecision.length} waiting for you`} color={C.accent} />}
          {round != null && maxRounds != null && (
            <span style={{ marginLeft: "auto", fontSize: 11, color: C.faint, fontVariantNumeric: "tabular-nums" }}>
              edit {round} / {maxRounds}
            </span>
          )}
        </div>
      </div>

      {/* Last edit feedback */}
      {lastEdit && (lastEdit.applied?.length || lastEdit.refused?.length || lastEdit.removed?.length) ? (
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
          {(lastEdit.applied || []).map((a, i) => (
            <div key={`a${i}`} style={{ color: C.good }}>✓ {a}</div>
          ))}
          {(lastEdit.removed || []).map((r, i) => (
            <div key={`r${i}`} style={{ color: C.muted, textDecoration: "line-through" }}>{r}</div>
          ))}
          {(lastEdit.refused || []).map((r, i) => (
            <div key={`x${i}`} style={{ color: C.warn }}>⚠ {r}</div>
          ))}
        </div>
      ) : null}

      {/* Please check first */}
      {needsDecision.length > 0 && (
        <Section title="Please check first">
          {needsDecision.map((d) => (
            <DecisionInput key={d.id} d={d} act={act} loading={loading || busy} />
          ))}
        </Section>
      )}

      {/* How I read your request */}
      {plan.interpretation && plan.interpretation.length > 0 && (
        <Section title="How I read your request">
          {plan.interpretation.map((it) => (
            <div key={it.id} style={{ fontSize: 12, color: C.text, lineHeight: 1.55, padding: "2px 0" }}>
              <span style={{ color: C.faint, fontWeight: 700, marginRight: 6 }}>{it.id}</span>{it.text}
              <SourceTag source={it.source} />
            </div>
          ))}
        </Section>
      )}

      {/* Flows */}
      {flows.length > 0 && (
        <Section title="Flows">
          {flows.map((f) => <FlowCard key={f.id} step={f} changedIds={changedIds} />)}
        </Section>
      )}

      {/* In every flow */}
      {effects && (effects.subSteps || []).length > 0 && (
        <Section title="In every flow">
          {effects.subSteps.map((e) => (
            <div key={e.id} style={{ fontSize: 12, color: C.text, lineHeight: 1.55, padding: "3px 0", background: changedIds.has(String(e.id)) ? C.hi : "transparent", borderRadius: 5 }}>
              <span style={{ color: C.faint, fontWeight: 700, marginRight: 6 }}>{e.id}</span>{e.text}
              {e.because && <span style={{ color: C.muted }}> <em>(because {e.because})</em></span>}
            </div>
          ))}
        </Section>
      )}

      {/* Systems */}
      {systems && (systems.subSteps || []).length > 0 && (
        <Section title={`Systems (${systems.subSteps.length})`}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {systems.subSteps.map((s) => (
              <div key={s.id} style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                <span style={{ color: C.faint, fontWeight: 700, marginRight: 6 }}>{s.id}</span>{s.text}
                <SourceTag source={s.source} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Decisions / Assumptions / Not included */}
      {plan.decisions && plan.decisions.length > 0 && (
        <Section title="Your decisions">
          {plan.decisions.map((d) => (
            <ListLine key={d.id} id={d.id} text={d.text} source={d.source} />
          ))}
        </Section>
      )}
      {plan.assumptions && plan.assumptions.length > 0 && (
        <Section title="Assumptions">
          {plan.assumptions.map((a) => (
            <ListLine key={a.id} id={a.id} text={a.text} source={a.source} />
          ))}
        </Section>
      )}
      {notes && (notes.subSteps || []).length > 0 && (
        <Section title="Not drawn as steps">
          {notes.subSteps.map((n) => (
            <ListLine key={n.id} id={n.id} text={n.text} source={n.source} />
          ))}
        </Section>
      )}
      {plan.notIncluded && plan.notIncluded.length > 0 && (
        <Section title="Not included">
          {plan.notIncluded.map((x) => (
            <ListLine key={x.id} id={x.id} text={x.text} />
          ))}
        </Section>
      )}

      <ActionBar text={text} setText={setText} sendTyped={sendTyped} act={act} loading={loading} busy={busy} canEdit={data && data.canEdit !== false} requests={requests} />
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: `${color}22`, color }}>
      {label}
    </span>
  );
}

function ListLine({ id, text, source }) {
  return (
    <div style={{ fontSize: 12, color: C.text, lineHeight: 1.55, padding: "2px 0" }}>
      <span style={{ color: C.faint, fontWeight: 700, marginRight: 6 }}>{id}</span>{text}
      <SourceTag source={source} />
    </div>
  );
}

function DecisionInput({ d, act, loading }) {
  const [v, setV] = React.useState("");
  // New contract: { id, key, question, suggestion }. Keep text/name as fallbacks
  // for older payloads — otherwise the row shows only the id with no prompt.
  const prompt = d.question || d.text || d.name || "";
  const submit = () => { if (v.trim()) { act(`${d.id}: ${v.trim()}`, { noBubble: false }); setV(""); } };
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
        <span style={{ color: C.warn, fontWeight: 700, marginRight: 6 }}>{d.id}</span>{prompt}
      </div>
      {d.suggestion && (
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>If you don’t answer: {d.suggestion}</div>
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
        <input
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder={d.suggestion ? `e.g. ${d.suggestion}` : "your choice…"}
          style={{ flex: 1, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, outline: "none" }}
        />
        <button
          onClick={submit}
          disabled={loading || !v.trim()}
          style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: C.accent, color: "#fff", fontSize: 11, fontWeight: 700, cursor: loading ? "default" : "pointer", opacity: loading || !v.trim() ? 0.5 : 1 }}
        >Set</button>
      </div>
    </div>
  );
}

function ActionBar({ text, setText, sendTyped, act, loading, busy, canEdit, requests }) {
  const disabled = loading || busy;
  const [expanded, setExpanded] = React.useState({}); // per-request "show more" toggles
  // Enter sends; Shift+Enter inserts a newline (so long/multi-line edits are easy to write).
  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTyped(); } };
  return (
    <div style={{ padding: "12px 16px", position: "sticky", bottom: 0, zIndex: 20, background: C.panel, borderTop: `1px solid ${C.border}` }}>
      {Array.isArray(requests) && requests.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Your requests</div>
          {/* Cap the echo so a long request can't push the plan out of view; long items
              collapse to 2 lines with a show more/less toggle, and the strip scrolls. */}
          <div style={{ maxHeight: 96, overflowY: "auto" }}>
            {requests.map((r, i) => {
              const long = (r || "").length > 120 || /\n/.test(r || "");
              const open = !!expanded[i];
              return (
                <div key={i} style={{ fontSize: 12, color: C.sub, lineHeight: 1.5, padding: "3px 8px", marginBottom: 3, background: "rgba(59,127,255,0.08)", borderRadius: 6 }}>
                  <span style={{ color: C.muted }}>you:</span>{" "}
                  <span style={long && !open ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", whiteSpace: "pre-wrap" } : { whiteSpace: "pre-wrap" }}>{r}</span>
                  {long && (
                    <button
                      onClick={() => setExpanded((p) => ({ ...p, [i]: !open }))}
                      style={{ display: "block", marginTop: 2, background: "transparent", border: "none", color: C.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 0 }}
                    >{open ? "▴ show less" : "▾ show more"}</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button
          onClick={() => act("approve", { noBubble: true })}
          disabled={disabled}
          style={{ flex: 1, padding: "9px", borderRadius: 7, background: C.accent, border: "none", color: "#fff", fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1 }}
        >✅ Approve &amp; draw diagram</button>
        <button
          onClick={() => act("undo", { noBubble: true })}
          disabled={disabled}
          style={{ padding: "9px 14px", borderRadius: 7, background: "transparent", border: `1px solid ${C.border}`, color: C.sub, fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1 }}
        >↶ Undo</button>
      </div>
      {canEdit !== false && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px"; }}
            onKeyDown={onKeyDown}
            rows={2}
            placeholder="Describe a change, ask a question, or type approve…  (Enter to send · Shift+Enter for newline)"
            disabled={disabled}
            style={{ flex: 1, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", fontSize: 12, outline: "none", resize: "none", minHeight: 38, maxHeight: 140, overflowY: "auto", fontFamily: "inherit", lineHeight: 1.5 }}
          />
          <button
            onClick={sendTyped}
            disabled={disabled || !text.trim()}
            style={{ padding: "8px 14px", borderRadius: 7, border: "none", background: "rgba(59,127,255,0.18)", color: C.accent, fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer", opacity: disabled || !text.trim() ? 0.5 : 1 }}
          >Send</button>
        </div>
      )}
      {disabled && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Working…</div>}
    </div>
  );
}
